import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { palette } from '../../../theme/paletteV2';
import type { PyramidDiagramRow, PyramidDiagramValueKey } from '../types';

type SeriesInfo = {
  label: string;
  color: string;
  dataKey: PyramidDiagramValueKey;
};

type TooltipEntry = {
  payload?: Partial<PyramidDiagramRow>;
};

type PyramidTooltipContentProps = {
  active?: boolean;
  label?: string | number;
  payload?: ReadonlyArray<TooltipEntry>;
  valueFormatter: (value: number) => string;
  left: SeriesInfo;
  right: SeriesInfo;
};

export const TooltipContent = ({ active, payload, label, valueFormatter, left, right }: PyramidTooltipContentProps) => {
  if (!active || !payload?.length) {
    return null;
  }

  const row = payload[0]?.payload;
  const series = [left, right];

  return (
    <Paper elevation={0} sx={{ p: 2, border: `1px solid ${palette.divider}` }} data-test="pyramid-diagram-tooltip">
      {label != null && (
        <Typography variant="body1Bold" sx={{ mb: 1 }} data-test="pyramid-diagram-tooltip-label">
          {label}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {series.map(item => {
          const raw = row?.[item.dataKey];
          if (typeof raw !== 'number' || !Number.isFinite(raw)) {
            return null;
          }

          return (
            <Box
              key={item.dataKey}
              sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}
              data-test="pyramid-diagram-tooltip-row"
            >
              <Box
                data-test="pyramid-diagram-tooltip-color"
                sx={{ width: 12, height: 12, backgroundColor: item.color, borderRadius: '2px', flexShrink: 0 }}
              />
              <Typography variant="body1" sx={{ display: 'flex', gap: 1 }}>
                <span data-test="pyramid-diagram-tooltip-series">{item.label}</span>
                <Typography variant="body1Bold" component="span" data-test="pyramid-diagram-tooltip-value">
                  {valueFormatter(Math.abs(raw))}
                </Typography>
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};
